import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import CardPeyment from "../../components/new_offers/CardPeyment";
import Footer2 from "../../components/footer/Footer2";
import { newGetOfferById } from "../../services/offers/offersApi";
import Loader from "../../components/loader/loader";
import { formateCoins } from "../../function";
import { usePayment } from "../../middleware/PaymentContext";
import { checkCoupon } from "../../services/coupons/couponsApi";
import { create_empty_achat } from "../../services/offers/payment";
import { getPaymentTypes } from "../../services/settings/paymentApi";
import axiosInstance from "../../services/server/axios";
import "./stripe.css";
import CryptomusPayment from "./payments/CryptomusPayment";
import PayopPayment from "./payments/PayopPayment";
import { useCurrency } from "../../context/CurrencyContext";


// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// WhatsApp Payment Component
const WhatsAppPayment = ({ offer, discount }) => {
  const { t } = useTranslation();
  const { formatPrice, currency } = useCurrency();

  const handleWhatsAppRedirect = () => {
    const message = `Offer: ${formateCoins(offer.coins)} coins\nAmount: ${formatPrice(offer.montant - discount)} ${currency}\nI want to purchase this offer.`;
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER}?text=${encodedMessage}`;

    window.open(whatsappUrl, "_blank");
  };

  return (
    <button
      onClick={handleWhatsAppRedirect}
      className="btn btn-success w-100"
      style={{ height: "48px", fontSize: "16px" }}
    >
      {t("ContactViaWhatsApp")}
    </button>
  );
};

// Stripe Payment Component
const StripePaymentComponent = ({ offer, discount, couponCode, onSuccess }) => {
  const [clientSecret, setClientSecret] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isDarkMode, setIsDarkMode] = useState(false);
  const { t } = useTranslation();
  const { currency, formatPrice } = useCurrency();

  useEffect(() => {
    if (offer) {
      createPaymentIntent();
    }
  }, [offer, discount, currency]);

  useEffect(() => {
    const checkDarkMode = () => {
      const isDark = document.body.classList.contains("is_dark");
      setIsDarkMode(isDark);
    };

    checkDarkMode();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === "class") {
          checkDarkMode();
        }
      });
    });

    observer.observe(document.body, { attributes: true });

    return () => observer.disconnect();
  }, []);

  const createPaymentIntent = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await axiosInstance.post(
        "/stripe/create-payment-intent",
        {
          amount: Math.round(formatPrice(offer.montant - discount) * 100),
          currency: currency.toLowerCase(),
          offer_id: offer.id,
        },
        {
          timeout: 15000,
        }
      );

      if (response.data.clientSecret) {
        setClientSecret(response.data.clientSecret);
      } else {
        throw new Error(
          t("payment.failed_initialize") || "Failed to initialize payment"
        );
      }
    } catch (error) {
      console.error("Error creating payment intent:", error);
      const errorMsg =
        error.response?.data?.error ||
        error.message ||
        t("payment.failed_initialize");
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    setError("");
    createPaymentIntent();
  };

  const getStripeAppearance = () => {
    if (isDarkMode) {
      return {
        theme: "night",
        labels: "floating",
        variables: {
          colorPrimary: "var(--input-color)",
          colorBackground: "var(--input-color)",
          colorText: "var(--input-color)",
          colorDanger: "var(--input-color)",
          colorTextPlaceholder: "var(--input-color)",
          colorIcon: "var(--input-color)",
          colorTextSecondary: "var(--input-color)",
          colorBorder: "var(--input-color)",
        },
      };
    } else {
      return {
        theme: "stripe",
      };
    }
  };

  if (loading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{t("payment.loading")}</span>
        </div>
        <p className="mt-2">{t("payment.initializing")}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger">
        <p className="mb-2">{error}</p>
        <button
          onClick={handleRetry}
          className="btn btn-sm btn-outline-primary"
        >
          {t("payment.retry_setup")}
        </button>
      </div>
    );
  }

  if (!clientSecret) {
    return null;
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: getStripeAppearance(),
        loader: "always",
      }}
    >
      <StripePaymentForm
        offer={offer}
        discount={discount}
        couponCode={couponCode}
        onSuccess={onSuccess}
        clientSecret={clientSecret}
      />
    </Elements>
  );
};

// Stripe Payment Form
const StripePaymentForm = ({
  offer,
  discount,
  couponCode,
  onSuccess,
  clientSecret,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { formatPrice, currency, getSymbol } = useCurrency();
  const [processing, setProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [paymentError, setPaymentError] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      toast.error("Payment system not ready. Please wait.");
      return;
    }

    setProcessing(true);
    setErrorMessage("");
    setPaymentError("");

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setErrorMessage(submitError.message);
        setProcessing(false);
        return;
      }

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/payment-success?method=stripe&offer_id=${offer.id}`,
        },
        redirect: "if_required",
      });

      if (error) {
        if (error.type === "card_error" || error.type === "validation_error") {
          setPaymentError(error.message);
        } else {
          setPaymentError("An unexpected error occurred.");
        }
        console.error("Stripe payment error:", error);
        setProcessing(false);
        return;
      }

      if (paymentIntent && paymentIntent.status === "succeeded") {
        await handlePaymentConfirmation(paymentIntent.id);
      } else if (!error) {
        setProcessing(false);
      }
    } catch (error) {
      console.error("Payment processing error:", error);
      setPaymentError("Payment processing failed. Please try again.");
      setProcessing(false);
    }
  };

  const handlePaymentConfirmation = async (paymentIntentId) => {
    try {
      const confirmResponse = await axiosInstance.post(
        "/stripe/confirm-payment",
        {
          payment_intent_id: paymentIntentId,
          offer_id: offer.id,
          coupon_code: couponCode,
          discount: discount,
        },
        {
          timeout: 10000,
        }
      );

      if (confirmResponse.data.success) {
        onSuccess({
          paymentID: paymentIntentId,
          method: "stripe",
          amount: formatPrice(offer.montant - discount),
          currency: currency,
          status: "succeeded",
          achat: confirmResponse.data.achat,
        });
      } else {
        throw new Error(
          confirmResponse.data.error || "Payment confirmation failed"
        );
      }
    } catch (error) {
      const errorMsg =
        error.response?.data?.error ||
        error.message ||
        "Payment confirmation failed";
      setPaymentError(errorMsg);
      toast.error(errorMsg);
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="stripe-payment-form">
      <div style={{ marginBottom: "16px" }}>
        <PaymentElement
          options={{
            layout: {
              type: "tabs",
              defaultCollapsed: false,
            },
          }}
        />
      </div>

      {errorMessage && (
        <div className="alert alert-warning mt-2" role="alert">
          {errorMessage}
        </div>
      )}

      {paymentError && (
        <div className="alert alert-danger mt-2" role="alert">
          {paymentError}
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || processing}
        className="btn btn-primary w-100"
        style={{
          height: "48px",
          fontSize: "16px",
          fontWeight: "600",
          opacity: processing ? 0.7 : 1,
        }}
      >
        {processing ? (
          <>
            <span
              className="spinner-border spinner-border-sm me-2"
              role="status"
            ></span>
            Processing...
          </>
        ) : (
          `Pay ${getSymbol()}${formatPrice(offer.montant - discount)}`
        )}
      </button>
    </form>
  );
};

// Payment Method Selector Component
const PaymentMethodSelector = ({
  paymentMethods,
  selectedMethod,
  onMethodSelect,
  offer,
  discount,
  couponCode,
  onPaymentSuccess,
}) => {
  const { t } = useTranslation();

  const getPaymentMethodIcon = (type) => {
    const icons = {
      stripe: "stripe",
      paypal: "paypal",
      cryptomus: "cryptomus",
      whatsapp: "whatsapp",
      pop: "payop",
    };
    return icons[type] || "💰";
  };

  const getPaymentMethodName = (type) => {
    const names = {
      stripe: t("payment_methods.stripe.name"),
      paypal: t("payment_methods.paypal.name"),
      cryptomus: t("payment_methods.cryptomus.name"),
      whatsapp: t("payment_methods.whatsapp.name"),
      pop: t("payment_methods.payop.name"),
    };
    return names[type] || type;
  };

  const getPaymentMethodDescription = (type) => {
    const descriptions = {
      stripe: t("payment_methods.stripe.description"),
      paypal: t("payment_methods.paypal.description"),
      whatsapp: t("payment_methods.whatsapp.description"),
      cryptomus: t("payment_methods.cryptomus.description"),
      pop: t("payment_methods.payop.description"),
    };
    return descriptions[type] || "";
  };

  return (
    <div className="payment-methods-container">
      <div className="payment-methods-list">
        {paymentMethods
          .filter((method) => {
            if (localStorage.getItem("role") === "superadmin") {
              return true;
            }
            return method.is_active;
          })
          .map((method) => (
            <div
              key={method.type}
              className={`payment-method-item ${selectedMethod?.type === method.type ? "active" : ""
                }`}
              onClick={() => onMethodSelect(method)}
              style={{
                border:
                  selectedMethod?.type === method.type
                    ? `2px solid ${t("colors.primary5", "var(--primary-color5)")}`
                    : "1px solid transparent",
                borderRadius: t("styles.borderRadius.medium", "16px"),
                marginBottom: t("spacing.medium", "16px"),
                overflow: "hidden",
                cursor: "pointer",
                backgroundColor: t("colors.primary8", "var(--primary-color8)"),
              }}
            >
              <div
                style={{
                  padding: t("spacing.medium", "16px"),
                  alignItems: "center",
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginRight: t("spacing.large", "23px"),
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      overflow: "hidden",
                      position: "relative",
                      alignItems: "center",
                      // background: t("colors.white", "rgb(255, 255, 255)"),
                      borderRadius: t("styles.borderRadius.small", "4px"),
                      justifyContent: "center",
                      width: t("sizes.icon.large", "52px"),
                      height: t("sizes.icon.large", "52px"),
                    }}
                  >
                    <span style={{ fontSize: t("fontSizes.large", "24px") }}>
                      <img
                        width={"40px"}
                        src={`/images/payments/${getPaymentMethodIcon(method.type)}.png`}
                        alt={t(
                          "accessibility.payment_icon",
                          "Payment method icon"
                        )}
                      />
                    </span>
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flex: 1,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <div
                      style={{
                        color: t("colors.input", "var(--input-color)"),
                        marginRight: t("spacing.xlarge", "24px"),
                      }}
                    >
                      {getPaymentMethodName(method.type)}
                    </div>
                    <div
                      style={{
                        color: t(
                          "colors.textSecondary",
                          "var(--text-secondary)"
                        ),
                      }}
                    >
                      {getPaymentMethodDescription(method.type)}
                    </div>
                  </div>
                  <div
                    style={{
                      border: `1px solid ${t("colors.border", "var(--border-color)")}`,
                      background:
                        selectedMethod?.type === method.type
                          ? t("colors.primary5", "var(--primary-color5)")
                          : "transparent",
                      borderRadius: "50%",
                      flexShrink: 0,
                      height: t("sizes.icon.small", "16px"),
                      width: t("sizes.icon.small", "16px"),
                      marginLeft: t("spacing.small", "8px"),
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

function NewSall() {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { formatPrice, currency, getSymbol } = useCurrency();
  const { completePayment } = usePayment();
  const [offer, setOffer] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [discount, setDiscount] = useState(0);
  const [couponCode, setCouponCode] = useState("");
  const [couponId, setCouponId] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Fetch offer data
        const offerResponse = await newGetOfferById(id);
        setOffer(offerResponse.data.offre);

        // Fetch payment methods
        const paymentResponse = await getPaymentTypes();
        const activeMethods = paymentResponse.data.payment_methods.filter(
          (method) =>
            method.is_active || localStorage.getItem("role") == "superadmin"
        );
        setPaymentMethods(activeMethods);

        // Set first active method as default
        if (activeMethods.length > 0) {
          setSelectedPaymentMethod(activeMethods[0]);
        }

        // Check coupon
        const currentCouponCode = localStorage.getItem("couponCode") ?? "";
        setCouponCode(currentCouponCode);
        if (currentCouponCode) {
          const couponResponse = await checkCoupon(
            currentCouponCode,
            offerResponse.data.offre.montant
          );
          setDiscount(couponResponse.discount);
          setCouponId(couponResponse.coupon_id);
        }
      } catch (error) {
        toast.error(t("Failed to load data"));
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [id, t]);

  const handlePaymentSuccess = async (paymentData) => {
    try {
      completePayment({
        ...paymentData,
        offerId: offer.id,
        couponCode: couponCode,
        discount: discount,
        amount: formatPrice(offer.montant - discount),
        currency: currency,
      });

      // For Cryptomus payments, we need to create the achat record
      if (paymentData.method === "cryptomus") {
        await create_empty_achat({
          offre_id: offer.id,
          payment_id: paymentData.paymentID,
          payer_id: paymentData.payerID || `cryptomus_${paymentData.paymentID}`,
          couponCode: couponCode,
          payment_method: "cryptomus",
          amount: formatPrice(offer.montant - discount),
          currency_payment: currency,
          country_code: paymentData.payerCountry || "",
          methodPayment: paymentData.method,
          cryptomus_payment_id: paymentData.paymentId, // Store the internal payment ID
        }).then((response) => {
          if (response.data?.achat?.id) {
            navigate(`/complete-purchase/${response.data?.achat?.id}`);
          } else {
            throw new Error(t("Purchase failed"));
          }
        });
      } else if (paymentData.method === "payop") {
        // For PayOP, the achat is already created by createInvoice
        if (paymentData.achat?.id) {
          navigate(`/complete-purchase/${paymentData.achat.id}`);
        } else {
          // Fallback if achat_id is missing
          navigate("/dashboard");
        }
      } else {
        // For other payment methods (existing logic)
        await create_empty_achat({
          offre_id: offer.id,
          payment_id: paymentData.paymentID,
          payer_id: paymentData.payerID || "",
          couponCode: couponCode,
          amount: formatPrice(offer.montant - discount),
          currency_payment: currency,
          formatted_price: `${formatPrice(offer.montant - discount)} ${currency}`,
          country_code: paymentData.payerCountry || "",
          methodPayment: paymentData.method,
          // method: paymentData.method,
          payment_method: "paypal",
        }).then((response) => {
          if (response.data?.achat?.id) {
            navigate(`/complete-purchase/${response.data?.achat?.id}`);
          } else {
            throw new Error(t("Purchase failed"));
          }
        });
      }
    } catch (error) {
      console.error("Payment completion error:", error);
      toast.error(error.message || t("Payment failed"));
    }
  };

  const handleMethodSelect = (method) => {
    setSelectedPaymentMethod(method);
  };

  const renderPaymentForm = () => {
    const role = localStorage.getItem("role");
    if (role != "superadmin") {
      if (!selectedPaymentMethod || !selectedPaymentMethod.is_active)
        return null;
    }

    switch (selectedPaymentMethod.type) {
      case "paypal":
        return (
          <PayPalScriptProvider
            options={{
              "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID,
              currency: currency,
              intent: "capture",
            }}
          >
            <PayPalButtons
              style={{
                layout: "vertical",
                color: "gold",
                shape: "rect",
                label: "paypal",
                height: 48,
              }}
              createOrder={(data, actions) => {
                return actions.order.create({
                  purchase_units: [
                    {
                      amount: {
                        value: formatPrice(offer.montant - discount),
                        currency_code: currency,
                      },
                    },
                  ],
                });
              }}
              onApprove={async (data, actions) => {
                try {
                  const details = await actions.order.capture();
                  handlePaymentSuccess({
                    paymentID: details.id,
                    payerID: details.payer.payer_id,
                    payerName: `${details.payer.name.given_name} ${details.payer.name.surname}`,
                    payerEmail: details.payer.email_address,
                    payerCountry: details.payer.address.country_code,
                    amount: details.purchase_units[0].amount.value,
                    currency: details.purchase_units[0].amount.currency_code,
                    status: details.status,
                    method: "paypal",
                  });
                } catch (error) {
                  console.error("Payment error:", error);
                  toast.error(error.message || t("Payment failed"));
                }
              }}
              onError={(err) => {
                console.error("PayPal error:", err);
                toast.error(t("Payment failed. Please try again"));
              }}
            />
          </PayPalScriptProvider>
        );

      case "stripe":
        return (
          <StripePaymentComponent
            offer={offer}
            discount={discount}
            couponCode={couponCode}
            onSuccess={handlePaymentSuccess}
          />
        );

      case "whatsapp":
        return <WhatsAppPayment offer={offer} discount={discount} />;

      case "cryptomus":
        return (
          <CryptomusPayment
            offer={offer}
            discount={discount}
            couponCode={couponCode}
            onSuccess={handlePaymentSuccess}
          />
        );

      // In your NewSall component, update the switch case:
      case "pop":
        return (
          <PayopPayment
            offer={offer}
            discount={discount}
            couponCode={couponCode}
            couponId={couponId}
            onSuccess={handlePaymentSuccess}
          />
        );

      default:
        return null;
    }
  };

  return (
    <>
      {isLoading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "80vh",
          }}
        >
          <Loader />
        </div>
      ) : (
        <div className="page-about home-1">
          <section className="tf-section tf-about">
            <div className="tf-container">
              <div className="row mb-3">
                <div className="col-lg-7">
                  {/* Product Card - Hidden on mobile, visible on desktop */}

                  <div
                    className="mx-3"
                    style={{
                      paddingBottom: "20px",
                    }}
                  >
                    {paymentMethods.length > 0 ? (
                      <PaymentMethodSelector
                        paymentMethods={paymentMethods}
                        selectedMethod={selectedPaymentMethod}
                        onMethodSelect={handleMethodSelect}
                        offer={offer}
                        discount={discount}
                        couponCode={couponCode}
                        onPaymentSuccess={handlePaymentSuccess}
                      />
                    ) : (
                      <div className="alert alert-warning text-center">
                        {t(
                          "No payment methods available. Please try again later."
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-lg-5">
                  {offer && (
                    <div className="new_offers_card pb-3 mobile-scroll">
                      <CardPeyment offer={offer} discount={discount} />
                      {selectedPaymentMethod && (
                        <div
                          className=""
                          style={{
                            marginRight: "10px",
                            marginLeft: "10px",
                            maxHeight: "300px",
                            padding: "5px 3px",
                            overflow: "hidden scroll",
                          }}
                        >
                          {renderPaymentForm()}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
          <Footer2 />
          <ToastContainer position="bottom-right" autoClose={5000} />
        </div>
      )}
    </>
  );
}

export default NewSall;
